import { useQuery } from '@apollo/client';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { GetCampaignDocument } from '@xing-com/crate-upsell-graphql';
import type { GetCampaignQuery } from '@xing-com/crate-upsell-graphql';
import { usePayOrConsentCampaignMid } from '@xing-com/crate-upsell-utils';

import { useLanguage } from './use-language';

type CardsCampaign = {
  campaigns: {
    premium: GetCampaignQuery['results'] | null;
    projobs: GetCampaignQuery['results'] | null;
  };
  errors: {
    premium?: Error;
    projobs?: Error;
  };
  loading: boolean;
  retry: () => Promise<void>;
};

export type CardsCampaignOptions = {
  mid: string;
  language: string;
};

export const useCardsCampaign = (
  product: ProductType,
  options?: CardsCampaignOptions
): CardsCampaign => {
  const hasExplicitContext = options !== undefined;
  const { loadingLanguage, language: viewerLanguage } = useLanguage({
    skip: hasExplicitContext,
  });
  const defaultMid = usePayOrConsentCampaignMid();
  const mid = options?.mid ?? defaultMid;
  const language = options?.language ?? viewerLanguage;
  const loadingContext = !hasExplicitContext && loadingLanguage;
  const skip = !product || loadingContext;
  const variables = { mid, language };

  const premiumCampaign = useQuery(GetCampaignDocument, {
    skip,
    variables: { ...variables, product: ProductType.Premium },
  });
  const projobsCampaign = useQuery(GetCampaignDocument, {
    skip,
    variables: { ...variables, product: ProductType.Projobs },
  });

  return {
    campaigns: {
      premium: premiumCampaign.data?.results ?? null,
      projobs: projobsCampaign.data?.results ?? null,
    },
    errors: {
      premium: premiumCampaign.error,
      projobs: projobsCampaign.error,
    },
    loading:
      loadingContext || premiumCampaign.loading || projobsCampaign.loading,
    retry: () =>
      Promise.all([premiumCampaign.refetch(), projobsCampaign.refetch()]).then(
        () => undefined
      ),
  };
};
